// Import city data
import citiesJawaBali from '../data/cities_regencies_jawa_bali.json';
import citiesSumatera from '../data/cities_regencies_sumatera.json';
import citiesSulawesi from '../data/cities_regencies_sulawesi.json';
import citiesKalimantan from '../data/cities_regencies_kalimantan.json';
import citiesMalukuPapua from '../data/cities_regencies_maluku_papua.json';
import citiesMalaysia from '../data/malaysia_cities.json';
import citiesSingapore from '../data/singapore_cities.json';
import citiesBrunei from '../data/brunei_cities.json';
import provincesIndonesia from '../data/provinces.json';
import provincesMalaysia from '../data/malaysia_provinces.json';
import provincesSingapore from '../data/singapore_provinces.json';
import provincesBrunei from '../data/brunei_provinces.json';

const allCitiesIndonesia = [
    ...citiesJawaBali,
    ...citiesSumatera,
    ...citiesSulawesi,
    ...citiesKalimantan,
    ...citiesMalukuPapua,
];

const allCitiesMalaysia = [...citiesMalaysia];
const allCitiesSingapore = [...citiesSingapore];
const allCitiesBrunei = [...citiesBrunei];

const allCities = [
    ...allCitiesIndonesia,
    ...allCitiesMalaysia,
    ...allCitiesSingapore,
    ...allCitiesBrunei,
];

const provinceNameById = new Map();
[
    ...provincesIndonesia,
    ...provincesMalaysia,
    ...provincesSingapore,
    ...provincesBrunei,
].forEach((p) => {
    if (p?.id != null && p?.nama) {
        provinceNameById.set(p.id, p.nama);
    }
});

const buildCityNameToRowMap = (rows) => {
    const map = new Map();
    rows.forEach((city) => {
        const fullName = city.nama;
        if (!fullName) return;
        if (!map.has(fullName)) {
            map.set(fullName, city);
        }
    });
    return map;
};

const indonesiaByName = buildCityNameToRowMap(allCitiesIndonesia);
const malaysiaByName = buildCityNameToRowMap(allCitiesMalaysia);
const singaporeByName = buildCityNameToRowMap(allCitiesSingapore);
const bruneiByName = buildCityNameToRowMap(allCitiesBrunei);

const COUNTRY_SUFFIXES = [
    ' (Brunei Darussalam)',
    '(Brunei Darussalam)',
    ' (Singapore)',
    '(Singapore)',
    ' (Malaysia)',
    '(Malaysia)',
];

const normalizeName = (cityName) =>
    cityName.toLowerCase().replace(/\s+/g, ' ').trim();

const stripCountryDisplaySuffix = (name) => {
    let s = (name || '').trim();
    for (const suffix of COUNTRY_SUFFIXES) {
        if (s.endsWith(suffix)) {
            s = s.slice(0, -suffix.length).trim();
        }
    }
    return s;
};

const partialMatchInMap = (normalizedName, nameToRow) => {
    const keys = Array.from(nameToRow.keys());
    keys.sort((a, b) => b.length - a.length);
    for (const key of keys) {
        const nk = normalizeName(key);
        if (nk.includes(normalizedName) || normalizedName.includes(nk)) {
            return nameToRow.get(key);
        }
    }
    return null;
};

const lookupCityRow = (cityName) => {
    if (!cityName) return null;
    const stripped = stripCountryDisplaySuffix(cityName);
    if (!stripped) return null;

    const maps = [
        { map: indonesiaByName, source: 'id' },
        { map: malaysiaByName, source: 'my' },
        { map: singaporeByName, source: 'sg' },
        { map: bruneiByName, source: 'bn' },
    ];

    for (const { map, source } of maps) {
        if (map.has(stripped)) {
            return { row: map.get(stripped), source };
        }
    }

    const nn = normalizeName(stripped);
    for (const { map, source } of maps) {
        const row = partialMatchInMap(nn, map);
        if (row) return { row, source };
    }
    return null;
};

const cityNameToIdMap = new Map();
allCitiesIndonesia.forEach((city) => {
    cityNameToIdMap.set(city.nama, city.id_kota_kabupaten);
});
[...allCitiesMalaysia, ...allCitiesSingapore, ...allCitiesBrunei].forEach((city) => {
    if (!cityNameToIdMap.has(city.nama)) {
        cityNameToIdMap.set(city.nama, city.id_kota_kabupaten);
    }
});

export const getCityId = (cityName) => {
    if (!cityName || !String(cityName).trim()) {
        return 999;
    }

    const hit = lookupCityRow(cityName);
    if (hit?.row?.id_kota_kabupaten != null) {
        return hit.row.id_kota_kabupaten;
    }

    if (cityNameToIdMap.has(cityName)) {
        return cityNameToIdMap.get(cityName);
    }

    const normalizedName = normalizeName(cityName);
    if (cityNameToIdMap.has(normalizedName)) {
        return cityNameToIdMap.get(normalizedName);
    }

    const partialMatches = Array.from(cityNameToIdMap.keys()).filter(
        (key) =>
            normalizeName(key).includes(normalizedName) ||
            normalizedName.includes(normalizeName(key)),
    );

    if (partialMatches.length > 0) {
        return cityNameToIdMap.get(partialMatches[0]);
    }

    return 999;
};

export const getCityName = (cityId) => {
    const city = allCities.find((c) => c.id_kota_kabupaten === cityId);
    return city ? city.nama : 'Unknown';
};

export const getProvinceNameById = (provinsiId) => {
    if (provinsiId == null || provinsiId === '') return '';
    const id = typeof provinsiId === 'string' ? parseInt(provinsiId, 10) : provinsiId;
    if (Number.isNaN(id)) return '';
    return provinceNameById.get(id) || '';
};

export const getProvinceFromCityName = (cityName) => {
    const hit = lookupCityRow(cityName);
    if (!hit?.row?.provinsi_id) return '';
    return getProvinceNameById(hit.row.provinsi_id) || '';
};

export const isIndonesianCity = (cityName) => {
    if (!cityName) return false;
    const hit = lookupCityRow(cityName);
    return hit?.source === 'id';
};

export const isMalaysianCity = (cityName) => {
    if (!cityName) return false;
    const hit = lookupCityRow(cityName);
    return hit?.source === 'my';
};

export const isSingaporeCity = (cityName) => {
    if (!cityName) return false;
    const hit = lookupCityRow(cityName);
    return hit?.source === 'sg';
};

export const isBruneiCity = (cityName) => {
    if (!cityName) return false;
    const hit = lookupCityRow(cityName);
    return hit?.source === 'bn';
};

const countryLabelForSource = (source) => {
    switch (source) {
        case 'my': return 'Malaysia';
        case 'sg': return 'Singapore';
        case 'bn': return 'Brunei Darussalam';
        default: return '';
    }
};

export const getCountryDisplaySuffix = (cityName) => {
    const hit = lookupCityRow(cityName);
    if (!hit) return '';
    return countryLabelForSource(hit.source);
};

export const formatCityWithCountry = (cityName) => {
    if (!cityName || cityName === 'N/A') {
        return 'N/A';
    }

    const base = stripCountryDisplaySuffix(cityName);
    const country = getCountryDisplaySuffix(cityName);
    if (!country) {
        return base;
    }

    const suffix = ` (${country})`;
    if (cityName.includes(suffix)) {
        return cityName;
    }
    return `${base}${suffix}`;
};
